import {useCallback} from 'react';
import {useUserStore} from '../store/userStore';
import {useRoleStore} from '../store/roleStore';
import {usePermissionStore} from '../store/permissionStore';
import {useUserUIStore} from '../store/userUIStore';
import {
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserPasswordRequest,
  AssignRoleRequest,
  RemoveRoleRequest,
  DashboardUser,
  UserStatus
} from '../models/user.model';
import {CreateRoleRequest} from '../models/role.model';

/**
 * Unified hook for user management following Clean Architecture principles
 *
 * This hook provides a clean interface for all user-related operations:
 * - User CRUD operations
 * - Role management
 * - Permission management
 * - UI state management
 *
 * Follows Single Responsibility Principle by delegating to appropriate stores
 */
export const useUserManagement = () => {
  // Data stores - each with single responsibility
  const userStore = useUserStore();
  const roleStore = useRoleStore();
  const permissionStore = usePermissionStore();
  const uiStore = useUserUIStore();

  // ===== USER MANAGEMENT OPERATIONS =====

  const handleCreateUser = useCallback(async (userData: CreateUserRequest): Promise<DashboardUser> => {
    try {
      const newUser = await userStore.createUser(userData);
      uiStore.setHasNewUser(true);
      return newUser;
    } catch (error) {
      throw error;
    }
  }, [userStore, uiStore]);

  const handleUpdateUser = useCallback(async (userData: UpdateUserRequest): Promise<DashboardUser> => {
    try {
      const updatedUser = await userStore.updateUser(userData);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  const handleDeleteUser = useCallback(async (userId: number): Promise<void> => {
    try {
      await userStore.deleteUser(userId);
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  const handleActivateUser = useCallback(async (userId: number): Promise<DashboardUser> => {
    try {
      const user = await userStore.activateUser(userId);
      return user;
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  const handleDeactivateUser = useCallback(async (userId: number): Promise<DashboardUser> => {
    try {
      const user = await userStore.deactivateUser(userId);
      return user;
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  const handleUpdateUserPassword = useCallback(async (passwordData: UpdateUserPasswordRequest): Promise<void> => {
    try {
      await userStore.updateUserPassword(passwordData);
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  // ===== ROLE MANAGEMENT OPERATIONS =====

  const handleCreateRole = useCallback(async (roleData: CreateRoleRequest) => {
    try {
      const newRole = await roleStore.createRole(roleData);
      return newRole;
    } catch (error) {
      throw error;
    }
  }, [roleStore]);

  const handleDeleteRole = useCallback(async (roleId: number): Promise<void> => {
    try {
      await roleStore.deleteRole(roleId);
    } catch (error) {
      throw error;
    }
  }, [roleStore]);

  const handleAssignRole = useCallback(async (roleData: AssignRoleRequest): Promise<DashboardUser> => {
    try {
      const updatedUser = await userStore.assignRole(roleData);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  const handleRemoveRole = useCallback(async (roleData: RemoveRoleRequest): Promise<DashboardUser> => {
    try {
      const updatedUser = await userStore.removeRole(roleData);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  // ===== PERMISSION MANAGEMENT OPERATIONS =====

  const handleAssignPermissions = useCallback(async (userId: number, permissions: string[]): Promise<void> => {
    try {
      await userStore.assignUserPermissions(userId, permissions);
    } catch (error) {
      throw error;
    }
  }, [userStore]);

  // ===== FILTER AND SEARCH OPERATIONS =====

  const handleSearch = useCallback((query: string) => {
    uiStore.setSearchQuery(query);
    const timer = setTimeout(() => {
      userStore.setFilters({...userStore.filters, search: query});
      userStore.fetchUsers();
    }, 500);
    return () => clearTimeout(timer);
  }, [uiStore, userStore]);

  const handleStatusFilter = useCallback((status: string) => {
    uiStore.setStatusFilter(status);
    userStore.setFilters({...userStore.filters, status: status as UserStatus || undefined});
    userStore.fetchUsers();
  }, [uiStore, userStore]);

  const handleRoleFilter = useCallback((roleId: number) => {
    uiStore.setRoleFilter(roleId);
    userStore.setFilters({
      ...userStore.filters,
      roleId: roleId === 0 ? undefined : roleId
    });
    userStore.fetchUsers();
  }, [uiStore, userStore]);

  // ===== MODAL MANAGEMENT OPERATIONS =====

  const openAddUserModal = useCallback(() => {
    uiStore.openAddUserModal();
  }, [uiStore]);

  const closeAddUserModal = useCallback(() => {
    uiStore.closeAddUserModal();
  }, [uiStore]);

  const openEditUserModal = useCallback((user: DashboardUser) => {
    uiStore.setSelectedUser(user);
    uiStore.openEditUserModal();
  }, [uiStore]);

  const closeEditUserModal = useCallback(() => {
    uiStore.closeEditUserModal();
    uiStore.setSelectedUser(null);
  }, [uiStore]);


  // ===== TAB MANAGEMENT OPERATIONS =====

  const switchTab = useCallback((tab: 'list' | 'statistics' | 'roles' | 'permissions') => {
    uiStore.setActiveTab(tab);
  }, [uiStore]);

  const setSelectedUser = useCallback((user: DashboardUser | null) => {
    uiStore.setSelectedUser(user);
  }, [uiStore]);

  // ===== DATA LOADING OPERATIONS =====

  const loadUsers = useCallback(async () => {
    try {
      await userStore.fetchAdmins();
    } catch (error) {
      // Error handling is done by the store
    }
  }, [userStore.fetchAdmins]);

  const loadRoles = useCallback(async () => {
    try {
      await roleStore.fetchRoles();
    } catch (error) {
      // Error handling is done by the store
    }
  }, [roleStore.fetchRoles]);

  const loadStatistics = useCallback(async () => {
    try {
      await userStore.fetchStatistics();
    } catch (error) {
      // Error handling is done by the store
    }
  }, [userStore.fetchStatistics]);

  // ===== COMPUTED VALUES =====

  const hasUsers = userStore.users.length > 0;
  const hasRoles = roleStore.roles.length > 0;

  // ===== RETURN CLEAN INTERFACE =====

  return {
    // Data
    users: userStore.users,
    roles: roleStore.roles,
    permissions: permissionStore.permissions,
    statistics: userStore.statistics,
    pagination: userStore.pagination,
    selectedUser: uiStore.selectedUser,
    hasUsers,
    hasRoles,

    // Loading states
    loading: userStore.loading,
    creating: userStore.creating,
    updating: userStore.updating,
    deleting: userStore.deleting,
    statisticsLoading: userStore.fetchingStatistics,
    rolesLoading: roleStore.loading,
    rolesCreating: roleStore.creating,
    rolesDeleting: roleStore.deleting,
    permissionsLoading: permissionStore.loading,

    // Error states
    error: userStore.error,
    createError: userStore.createError,
    updateError: userStore.updateError,
    deleteError: userStore.deleteError,
    rolesError: roleStore.error,
    rolesCreateError: roleStore.createError,
    rolesDeleteError: roleStore.deleteError,
    permissionsError: permissionStore.error,

    // UI state
    activeTab: uiStore.activeTab,
    searchQuery: uiStore.searchQuery,
    statusFilter: uiStore.statusFilter,
    roleFilter: uiStore.roleFilter,
    isAddUserModalOpen: uiStore.isAddUserModalOpen,
    isEditUserModalOpen: uiStore.isEditUserModalOpen,

    // User operations
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleActivateUser,
    handleDeactivateUser,
    handleUpdateUserPassword,
    handleAssignPermissions,

    // Role operations
    handleCreateRole,
    handleDeleteRole,
    assignRole: handleAssignRole,
    removeRole: handleRemoveRole,

    // Filter operations
    search: handleSearch,
    filterByStatus: handleStatusFilter,
    filterByRole: handleRoleFilter,

    // Modal operations
    openAddUserModal,
    closeAddUserModal,
    openEditUserModal,
    closeEditUserModal,
    openPermissionsModal: uiStore.openPermissionsModal,
    closePermissionsModal: uiStore.closePermissionsModal,

    // Tab operations
    switchTab,

    // Selection operations
    setSelectedUser,
    setManagingPermissionsRole: uiStore.setManagingPermissionsRole,

    // Form states
    hasNewUser: uiStore.hasNewUser,

    // Modal states
    isPermissionsModalOpen: uiStore.isPermissionsModalOpen,
    managingPermissionsRole: uiStore.managingPermissionsRole,

    // Search and filter setters
    setSearchQuery: uiStore.setSearchQuery,
    setStatusFilter: uiStore.setStatusFilter,
    setRoleFilter: uiStore.setRoleFilter,

    // Data loading
    loadUsers,
    loadRoles,
    loadStatistics,

    // Utility operations
    clearError: userStore.clearError,
    clearCreateError: userStore.clearCreateError,
    clearUpdateError: userStore.clearUpdateError,
    clearDeleteError: userStore.clearDeleteError,
    clearRolesError: roleStore.clearError,
    reset: userStore.reset
  };
};
